(() => {
  'use strict';

  // Stack the native chat icon and label inside one high-contrast round button.
  // Styling is injected as a stylesheet into each Shopify Chat shadow root so it
  // survives the widget's own re-renders without per-mutation restyling or flicker.
  const STYLE_MARKER = 'data-chat-launcher-icon-inside';
  const styledRoots = new WeakSet();
  const observedRoots = new WeakSet();
  let retryTimer = null;

  const CSS = `
    button.activator {
      width: 64px !important;
      min-width: 64px !important;
      height: 64px !important;
      min-height: 64px !important;
      padding: 6px !important;
      border-radius: 50% !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 2px !important;
      background: #FAF9F9 !important;
      background-image: none !important;
      color: #111111 !important;
      border: 1px solid #747474 !important;
      box-shadow: none !important;
    }

    button.activator .activator__icon {
      width: 23px !important;
      height: 23px !important;
      min-width: 23px !important;
      margin: 0 !important;
      color: #111111 !important;
    }

    button.activator .activator__icon svg {
      width: 100% !important;
      height: 100% !important;
      color: #111111 !important;
    }

    button.activator .activator__label {
      display: block !important;
      margin: 0 !important;
      font-size: 12px !important;
      line-height: 1 !important;
      letter-spacing: 0 !important;
      white-space: nowrap !important;
      font-weight: 600 !important;
      color: #111111 !important;
    }

    @media (max-width: 767px) {
      button.activator {
        width: 60px !important;
        min-width: 60px !important;
        height: 60px !important;
        min-height: 60px !important;
      }

      /* Lift the closed launcher clear of the theme's fixed 60px bottom dock.
         The margin goes on the positioned wrapper rather than the button: on the
         button it would grow the wrapper downwards instead of moving it, leaving
         a tap-swallowing band over the dock. Adds to Shopify's own bottom offset. */
      .activator-nudge {
        margin-bottom: 64px !important;
      }
    }
  `;

  const getRoots = () => {
    const hostRoot = document.querySelector('shopify-chat')?.shadowRoot;
    const runtimeRoot = hostRoot?.querySelector('shopify-agent-runtime')?.shadowRoot;
    return [hostRoot, runtimeRoot].filter(Boolean);
  };

  const injectStyle = (root) => {
    if (styledRoots.has(root) || root.querySelector(`style[${STYLE_MARKER}]`)) return;
    const style = document.createElement('style');
    style.setAttribute(STYLE_MARKER, 'true');
    style.textContent = CSS;
    root.appendChild(style);
    styledRoots.add(root);
  };

  const markLauncher = () => {
    const button = getRoots()
      .map((root) => root.querySelector('button.activator'))
      .find(Boolean);
    if (!button) return false;
    button.dataset.iconInside = 'true';
    return true;
  };

  const attachObservers = () => {
    const hostRoot = document.querySelector('shopify-chat')?.shadowRoot;
    if (!hostRoot) {
      retryTimer = window.setTimeout(attachObservers, 100);
      return;
    }

    const roots = getRoots();
    roots.forEach((root) => {
      injectStyle(root);
      if (observedRoots.has(root)) return;
      observedRoots.add(root);
      // Only watch for a new shadow root / launcher appearing; styling itself is
      // handled by the injected stylesheet, so this observer stays cheap.
      new MutationObserver(() => {
        getRoots().forEach(injectStyle);
        markLauncher();
      }).observe(root, { childList: true, subtree: true });
    });

    markLauncher();

    const runtime = hostRoot.querySelector('shopify-agent-runtime');
    if (runtime && !runtime.shadowRoot) {
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(attachObservers, 100);
    }
  };

  const start = () => {
    customElements.whenDefined('shopify-chat').then(attachObservers);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
